<template>
  <b-card no-body>
    <b-row class="my-3 mx-2">
      <b-col>
        <strong v-if="!!invoices">
          {{ invoices.length }}
          {{ trans('invoice' + (invoices.length > 1 || invoices.length == 0 && lang == 'en' ? 's' : '')).toLowerCase() }}
        </strong>
        <small class="d-block">
          {{ trans('questions') }}?
          <b-link v-if="!!organization.email" :href="`mailto:${organization.email}`">
            {{ organization.email }}
          </b-link>
          <b-link v-if="!!organization.phone" :href="`tel:${organization.phone}`">
            {{ organization.phone }}
          </b-link>
        </small>
      </b-col>
      <b-col class="col-auto">
        <b-button v-if="organization.has_stripe" variant="outline-primary" size="sm" class="mr-2" :disabled="busy" v-b-modal.client-credit-card-on-file-modal>
          <i class="fa fa-fw fa-credit-card"></i>
          {{ trans('cc_on_file') }}
        </b-button>
        <b-button variant="outline-success" size="sm" target="_blank" :href="downloadExcelUrl" :disabled="busy">
          <i class="fa fa-fw fa-file-excel"></i>
          {{ trans('export') }}
        </b-button>
      </b-col>
    </b-row>
    <b-table
      id="invoices"
      ref="table"
      class="bg-white"
      responsive
      show-empty
      :per-page="perPage"
      :current-page="currentPage"
      :fields="fields"
      :items="invoices"
    >
      <template #empty>
        <p align="center">
          <span v-if="!busy">{{ trans('none') }}</span>
          <span v-if="busy">
            <i class="fa fa-fw fa-circle-notch fa-spin" v-if="busy"></i>
            {{ trans('please_wait') }}
          </span>
        </p>
      </template>
      <template #cell(invoice_no)="data">
        <b-link :href="getPdfUrl(data.item)" target="_blank" :title="trans('download_pdf')" v-b-tooltip.hover.right>
          {{ data.value }}
        </b-link>
      </template>
      <template #cell(created_at)="data">
        <small>{{ data.value }}</small>
      </template>
      <template #cell(invoice_items)="data">
        <div v-for="ii in data.value" :key="ii.id">
          <small v-html="ii.name"></small>
          <small v-if="ii.is_cancelled" class="text-danger">{{ trans('cancelled') }}</small>
        </div>
        <div v-if="data.item.has_interac_pending" class="mt-1">
          <small class="text-danger">{{ trans('interac_pending_about') }}</small>
          <b-link @click="invoice = data.item; $bvModal.show('interac-instructions-modal')">
            <small>
              <i class="fa fa-fw fa-eye"></i>
              {{ trans('reveal_interac_instructions') }}
            </small>
          </b-link>
        </div>
      </template>
      <template #cell(total)="data">
        {{ currency(data.value) }}
      </template>
      <template #cell(paid_amount)="data">
        {{ currency(data.value) }}
      </template>
      <template #cell(balance)="data">
        <span :class="!data.value ? '' : (data.value > 0 ? 'text-danger' : 'text-success')">{{ currency(data.value) }}</span>
        <b-button v-if="data.value > 0" variant="branded" size="sm" class="ml-2 mt-2 py-0" :href="data.item.pay_balance_url" target="_blank">
          {{ trans('pay') }}
        </b-button>
      </template>
      <template #cell(action)="data">
        <b-dropdown
            variant="link"
            size="sm"
            class="p-0"
            toggle-class="text-muted"
            boundary="window"
            right
            :disabled="busy"
          >
            <template #button-content>
              <i class="fa fa-fw fa-ellipsis-h"></i>
            </template>
            <b-dropdown-item
              :href="getPdfUrl(data.item)"
              target="_blank"
            >
              {{ trans('download_pdf') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.has_classes"
              @click="addToCalendar(data.item)"
            >
              {{ trans('add_to_calendar') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.balance > 0"
              @click="payBalance(data.item)"
            >
              {{ trans('invoice_pay_balance') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canBuyAddons(data.item)"
              @click="buyAddons(data.item)"
            >
              {{ trans('buy_addons') }}
            </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>
    <b-pagination
      :total-rows="totalRows"
      :per-page="perPage"
      v-model="currentPage"
      aria-controls="invoices"
      align="center"
    ></b-pagination>
    <client-credit-card-on-file-modal
      :organization="organization"
      :client="client"
    ></client-credit-card-on-file-modal>
    <interac-instructions-modal
      :organization="organization"
      :invoice="invoice"
      :url="`/org/${this.organization.id}/client/interac/${this.invoice ? this.invoice.invoice_no : null}`"
    ></interac-instructions-modal>
    <BuyAddonsModal
      :organization="organization"
      :recipient_id="recipient_id"
    />
  </b-card>
</template>

<script>
  import InteracInstructionsModal from './../interac/InteracInstructionsModal'
  import ClientCreditCardOnFileModal from './ClientCreditCardOnFileModal'
  import BuyAddonsModal from './BuyAddonsModal'

  export default {
    props: {
      organization: Object,
      client: Object,
    },
    components: {
      InteracInstructionsModal,
      ClientCreditCardOnFileModal,
      BuyAddonsModal,
    },
    data() {
      return {
        trans: trans,
        lang: lang,
        currency: _.currency,
        maybeAddSession: _.maybeAddSession,
        busy: false,
        error: null,
        invoice: null,
        fields: [
          {
            key: 'invoice_no',
            label: trans('invoice'),
          },
          {
            key: 'created_at',
            label: trans('date'),
            formatter(value, key, item) {
              return value ? moment(value).format('LLL') : null
            },
            sortable: true,
          },
          {
            key: 'invoice_items',
            label: trans('description'),
          },
          {
            key: 'total',
            label: trans('total'),
          },
          {
            key: 'paid_amount',
            label: trans('paid'),
          },
          {
            key: 'balance',
            label: trans('balance'),
          },
          {key: 'action', label: ''},
        ],
        invoices: null,
        perPage: 10,
        currentPage: 1,
        totalRows: 0,
        recipient_id: null,
      }
    },
    computed: {
      downloadExcelUrl() {
        return _.maybeAddSession(`/org/${this.organization.id}/client/invoices/excel`, this.organization)
      },
    },
    methods: {
      buyAddons(item) {
        this.recipient_id = item.recipient_id
        this.$bvModal.show('buy-addons-modal')
      },
      canBuyAddons(item) {
        if (!item.invoice_items) return false
        return item.invoice_items.some(invoiceItem =>
          invoiceItem.am_class_meta && invoiceItem.am_class_meta.sale_addons
        )
      },
      payBalance(item) {
        return window.location.href = item.pay_balance_url
      },
      addToCalendar(item) {
        return _.maybeAddSession(`/org/${this.organization.id}/calendar/invoice/${item.ucid}/add_to_calendar`, this.organization)
      },
      getPdfUrl(item) {
        return _.maybeAddSession(item.invoice_url, this.organization)
      },
      fetch() {
        this.busy = true
        this.error = null
        let query = `?page=${this.currentPage}&per_page=${this.perPage}`
        let url = `/org/${this.organization.id}/client/invoices${query}`
        return axios.get(url)
        .then(response => {
          this.busy = false
          this.invoices = response.data
          this.totalRows = this.invoices.length
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          this.$bvToast.toast(this.error, {
            title: trans('error'),
            variant: 'danger',
            solid: true,
          })
          this.invoices = []
        })
      },
    },
    mounted() {
      window.myInvoices = this
      this.fetch()
    },
  }
</script>
